import React from "react";
import { useObserver } from "mobx-react-lite";
import {
  Grid,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Switch,
} from "@material-ui/core";
import { TrackingVendor } from "api";
import { AsyncButton, Select } from "components";
import { useStore } from "./Store";
import { useFormStore } from "./FormStore";

interface Props {
  agencyId: number;
}

export default ({ agencyId }: Props) => {
  const store = useStore();
  const formStore = useFormStore();

  const handleAppGroup = React.useCallback(
    (value) => {
      if (value) {
        formStore.value.appGroupId = value.value;
        const apps = formStore.appGroup?.apps || [];
        formStore.value.trackingVendorId = apps[0]?.trackingVendor?.id;
        formStore.value.ios.id = apps.find((app) => app.os === "ios")?.id;
        formStore.value.android.id = apps.find(
          (app) => app.os === "android"
        )?.id;
      } else {
        formStore.value.trackingVendorId = undefined;
        formStore.value.appGroupId = undefined;
        formStore.value.ios = {};
        formStore.value.android = {};
      }
    },
    [formStore]
  );

  const handleAppIos = React.useCallback(
    (appId: number) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
          formStore.value.ios.id = appId;
        } else {
          formStore.value.ios.id = undefined;
        }
      };
    },
    [formStore]
  );

  const handleAppAndroid = React.useCallback(
    (appId: number) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
          formStore.value.android.id = appId;
        } else {
          formStore.value.android.id = undefined;
        }
      };
    },
    [formStore]
  );

  const handleIosRedDmpAppId = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.ios.redDmpAppId = +e.target.value;
    },
    [formStore]
  );

  const handleAndroidRedDmpAppId = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.android.redDmpAppId = +e.target.value;
    },
    [formStore]
  );

  const handleRedUserId = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.redUserId = +e.target.value;
    },
    [formStore]
  );

  const handleTwitterUserId = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.twitterUserId = e.target.value;
    },
    [formStore]
  );

  const handleIsTver = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.isTver = e.target.checked;
    },
    [formStore]
  );

  const save = React.useCallback(async () => {
    if (await formStore.save(agencyId)) {
      store.fetchAppGroups(agencyId);
    }
  }, [store, formStore, agencyId]);

  return useObserver(() => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Select
          label="アプリ"
          placeholder="アプリを選択してください"
          isClearable
          isDisabled={formStore.edit}
          onChange={handleAppGroup}
          value={
            formStore.appGroup
              ? {
                  label: formStore.appGroup.name,
                  value: formStore.appGroup.id,
                }
              : null
          }
          options={formStore.appGroups.map((appGroup) => ({
            label: appGroup.name,
            value: appGroup.id,
          }))}
        />
      </Grid>

      {formStore.appGroup && (
        <>
          <Grid item xs={6}>
            <FormControl>
              <FormLabel>OS</FormLabel>
              <FormGroup>
                {formStore.appGroup.apps
                  .filter((app) => app.os === "ios")
                  .map((app) => (
                    <FormControlLabel
                      key={app.id}
                      label="iOS"
                      control={
                        <Checkbox
                          onChange={handleAppIos(app.id)}
                          checked={formStore.value.ios.id ? true : false}
                        />
                      }
                    />
                  ))}
                {formStore.appGroup.apps
                  .filter((app) => app.os === "android")
                  .map((app) => (
                    <FormControlLabel
                      key={app.id}
                      label="Android"
                      control={
                        <Checkbox
                          onChange={handleAppAndroid(app.id)}
                          checked={formStore.value.android.id ? true : false}
                        />
                      }
                    />
                  ))}
              </FormGroup>
            </FormControl>
          </Grid>

          {formStore.value.trackingVendorId === TrackingVendor.APPS_FLYER && (
            <Grid item xs={6}>
              <FormControl>
                <FormLabel>Red アプリ ID</FormLabel>
                <FormGroup>
                  <TextField
                    type="number"
                    fullWidth
                    label="iOS"
                    value={formStore.value.ios.redDmpAppId || ""}
                    error={!!formStore.errors.columns.apps}
                    helperText={formStore.errors.columns.apps}
                    onChange={handleIosRedDmpAppId}
                  />
                  <TextField
                    type="number"
                    fullWidth
                    label="Android"
                    value={formStore.value.android.redDmpAppId || ""}
                    error={!!formStore.errors.columns.apps}
                    helperText={formStore.errors.columns.apps}
                    onChange={handleAndroidRedDmpAppId}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Red 広告主 ID"
              value={formStore.value.redUserId || ""}
              error={!!formStore.errors.columns.redUserId}
              helperText={formStore.errors.columns.redUserId}
              onChange={handleRedUserId}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormLabel>TVer配信</FormLabel>
              <Switch
                color="primary"
                name="isTver"
                checked={formStore.value.isTver}
                onChange={handleIsTver}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Twitter アカウント ID"
              value={formStore.value.twitterUserId || ""}
              error={!!formStore.errors.columns.twitterUserId}
              helperText={formStore.errors.columns.twitterUserId}
              onChange={handleTwitterUserId}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <AsyncButton onAwait={save}>保存</AsyncButton>
      </Grid>
    </Grid>
  ));
};
